import { Injectable } from '@angular/core';

@Injectable({
  providedIn: 'root'
})
export class CallAudioService {
  private audioContext: AudioContext | null = null;
  private ringbackAudio: HTMLAudioElement | null = null;
  private oscillatorNodes: OscillatorNode[] = [];
  private gainNodes: GainNode[] = [];
  private ringtoneTimerId: number | null = null;
  private isCurrentlyPlaying = false;
  private volumeLevel = 0.5;

  /**
   * Start playing outbound call ringback tone
   * @returns Promise<boolean> - true if audio started successfully
   */
  async playOutboundRingback(): Promise<boolean> {
    this.stopAllAudio();

    const webAudioReady = await this.initAudioContext();
    if (webAudioReady) {
      this.createRingtonePattern();
      this.isCurrentlyPlaying = true;
      return true;
    }

    return this.playRingbackWithHTML5Audio();
  }

  /**
   * Start playing inbound call ringtone
   * @param options - Optional ringtone configuration
   * @returns Promise<boolean> - true if audio started successfully
   */
  async startRingtone(options?: { audioFile?: string; loop?: boolean; volume?: number; vibrate?: boolean }): Promise<boolean> {
    this.stopAllAudio();

    const audioFile = options?.audioFile || '/assets/audio/notification.mp3';
    const loop = options?.loop !== undefined ? options.loop : true;
    const volume = options?.volume !== undefined ? options.volume : 1.0;
    const vibrate = options?.vibrate || false;

    // Trigger vibration if requested and supported
    if (vibrate && 'vibrate' in navigator) {
      try {
        navigator.vibrate([200, 100, 200, 100, 200]);
      } catch (e) {
        // Vibration not supported or failed
      }
    }

    try {
      this.ringbackAudio = new Audio(audioFile);
      this.ringbackAudio.loop = loop;
      this.ringbackAudio.volume = Math.max(0, Math.min(1, volume));

      await this.ringbackAudio.play();
      this.isCurrentlyPlaying = true;
      return true;
    } catch (e) {
      console.error('Ringtone playback failed:', e);
      this.ringbackAudio = null;
      return false;
    }
  }

  /**
   * Stop ringtone playback (alias for stopAllAudio for backward compatibility)
   */
  stopRingtone(): void {
    this.stopAllAudio();
  }

  /**
   * Stop all audio playback immediately
   */
  stopAllAudio(): void {
    if (this.ringtoneTimerId) {
      clearTimeout(this.ringtoneTimerId);
      this.ringtoneTimerId = null;
    }

    this.oscillatorNodes.forEach(osc => {
      try {
        osc.stop();
        osc.disconnect();
      } catch (e) {
        // Oscillator may already be stopped
      }
    });
    this.oscillatorNodes = [];

    this.gainNodes.forEach(gain => {
      try {
        gain.disconnect();
      } catch (e) {
        // Node may already be disconnected
      }
    });
    this.gainNodes = [];

    if (this.ringbackAudio) {
      this.ringbackAudio.pause();
      this.ringbackAudio.currentTime = 0;
      this.ringbackAudio.src = '';
      this.ringbackAudio = null;
    }

    // Cancel any ongoing vibration
    if ('vibrate' in navigator) {
      try {
        navigator.vibrate(0);
      } catch (e) {
        // Vibration cancellation failed - browser may not support it
      }
    }

    this.isCurrentlyPlaying = false;
  }

  /**
   * Check if audio is currently playing
   */
  isPlaying(): boolean {
    return this.isCurrentlyPlaying;
  }

  /**
   * Set audio volume level
   * @param volume - Volume level from 0.0 to 1.0
   */
  setVolume(volume: number): void {
    this.volumeLevel = Math.max(0, Math.min(1, volume));

    if (this.ringbackAudio) {
      this.ringbackAudio.volume = this.volumeLevel;
    }

    this.gainNodes.forEach(gain => {
      try {
        gain.gain.value = this.volumeLevel;
      } catch (e) {
        // Node may be disconnected
      }
    });
  }

  /**
   * Initialize Web Audio API context
   */
  private async initAudioContext(): Promise<boolean> {
    if (this.audioContext) {
      if (this.audioContext.state === 'suspended') {
        try {
          await this.audioContext.resume();
        } catch (e) {
          console.error('Failed to resume audio context:', e);
          return false;
        }
      }
      return this.audioContext.state === 'running';
    }

    try {
      const AudioContextClass = window.AudioContext || (window as any).webkitAudioContext;
      if (!AudioContextClass) {
        return false;
      }
      this.audioContext = new AudioContextClass();
      return this.audioContext.state === 'running';
    } catch (e) {
      console.error('Failed to create audio context:', e);
      return false;
    }
  }

  /**
   * Create a tone burst with specified frequencies
   */
  private createToneBurst(freq1: number, freq2: number, startTime: number, duration: number): void {
    if (!this.audioContext) return;

    const gain = this.audioContext.createGain();
    gain.gain.value = this.volumeLevel;
    gain.connect(this.audioContext.destination);
    this.gainNodes.push(gain);

    const createOscillator = (frequency: number): OscillatorNode => {
      if (!this.audioContext) throw new Error('AudioContext not initialized');

      const osc = this.audioContext.createOscillator();
      osc.type = 'sine';
      osc.frequency.value = frequency;
      osc.connect(gain);

      osc.onended = () => {
        const oscIndex = this.oscillatorNodes.indexOf(osc);
        if (oscIndex > -1) {
          this.oscillatorNodes.splice(oscIndex, 1);
        }
      };

      this.oscillatorNodes.push(osc);
      return osc;
    };

    try {
      const osc1 = createOscillator(freq1);
      const osc2 = createOscillator(freq2);

      osc1.start(startTime);
      osc1.stop(startTime + duration);
      osc2.start(startTime);
      osc2.stop(startTime + duration);
    } catch (e) {
      console.error('Failed to create tone burst:', e);
    }
  }

  /**
   * Create the ringtone pattern: E5 + B5 harmony with proper timing
   * Pattern: 0.4s tone, 0.2s pause, 0.4s tone, 1.0s silence, repeat
   */
  private createRingtonePattern(): void {
    if (!this.audioContext) return;

    const E5 = 659.25;
    const B5 = 987.77;
    const now = this.audioContext.currentTime;

    // First burst
    this.createToneBurst(E5, B5, now, 0.4);
    // Second burst (after 0.6s = 0.4s tone + 0.2s pause)
    this.createToneBurst(E5, B5, now + 0.6, 0.4);

    // Schedule next pattern (after 2.0s total cycle)
    this.ringtoneTimerId = window.setTimeout(() => {
      if (this.isCurrentlyPlaying && this.audioContext) {
        this.createRingtonePattern();
      }
    }, 2000);
  }

  /**
   * Fallback to HTML5 Audio element
   */
  private async playRingbackWithHTML5Audio(): Promise<boolean> {
    try {
      this.ringbackAudio = new Audio('/assets/audio/ringtone.wav');
      this.ringbackAudio.loop = true;
      this.ringbackAudio.volume = this.volumeLevel;

      await this.ringbackAudio.play();
      this.isCurrentlyPlaying = true;
      return true;
    } catch (e) {
      console.error('HTML5 audio playback failed:', e);
      this.ringbackAudio = null;
      return false;
    }
  }
}
